/* nplus-buybox.js — <nplus-buybox> controller.
   Pill variant picker (syncs hidden select + price), Add-to-cart bundle (bike + gift + accessories),
   and the exclusive deposit/leasing accordion (wired in a later task). Classic script. */
(function () {
  'use strict';

  class NplusBuybox extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.select = this.querySelector('[data-nplus-bike-variant]');
      this.priceEl = this.querySelector('[data-nplus-price]');
      this.atc = this.querySelector('[data-nplus-atc]');
      this.errorEl = this.querySelector('[data-nplus-error]');
      this.gift = this.querySelector('nplus-gift-bundle');
      this.stockEl = this.querySelector('[data-nplus-stock]');
      this.stockTextEl = this.querySelector('[data-nplus-stock-text]');

      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-variant-pill]'), function (pill) {
        pill.addEventListener('click', function () { self.selectPill(pill); });
      });
      if (this.atc) this.atc.addEventListener('click', this.onAdd.bind(this));

      // Cash / Finance "buy now" buttons: add the selection, go straight to checkout.
      this.buyNow = Array.prototype.slice.call(this.querySelectorAll('[data-nplus-buynow]'));
      this.buyNow.forEach(function (btn) {
        btn.addEventListener('click', function () { self.onBuyNow(btn.getAttribute('data-method')); });
      });
      // Back from the Stripe page via the browser's page cache: the buttons would still be busy.
      window.addEventListener('pageshow', function (e) { if (e.persisted) self.setBuyNowBusy(false); });
      this.showOrderNotice();

      this.initReveals(); // accordion (defined below; no-op if no reveal buttons)
      this.initTabs();    // Cash/Lease/Finance switcher (no-op if no tabs)
      this.activateHashTab(); // Deep links such as #nplus-tab-finance select the requested tab
      this.initLeaseTeasers(); // "Dienstrad-Leasing ab …" links: Lease tab + form (drawer only as fallback)
      this.initJobradWidget(); // JobRad logo trigger(s) + live price sync (no-op if none rendered)
      this.initMoney(); // Intl formatter for the subtotal, from .nplus-bb__i18n's currency/locale

      // Subtotal recompute triggers: bike-variant change (pill click, below) and accessory
      // selection change (dispatched by nplus-accessories.js). Bound before the initial
      // emitBikeVariant()/recalcSubtotal() calls so nothing fired during setup is missed.
      document.addEventListener('nplus:bike-variant-change', function () { self.recalcSubtotal(); });
      document.addEventListener('nplus:accessories-change', function () { self.recalcSubtotal(); });

      this.emitBikeVariant(); // initial bike size → size-linked accessories
      this.recalcSubtotal();  // initial subtotal paint, once listeners are bound
    }

    selectPill(pill) {
      if (pill.getAttribute('aria-disabled') === 'true') return;
      var id = pill.getAttribute('data-variant');
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-variant-pill]'), function (p) {
        p.classList.toggle('is-selected', p === pill);
      });
      if (this.select) { this.select.value = id; }
      if (this.priceEl) {
        this.priceEl.textContent = pill.getAttribute('data-price') || this.priceEl.textContent;
        var minor = pill.getAttribute('data-price-minor');
        if (minor != null) this.priceEl.setAttribute('data-price-minor', minor);
      }
      this.updateStock(id);
      this.clearError();
      this.emitBikeVariant(); // dispatches nplus:bike-variant-change → recalcSubtotal via listener above
    }

    // Notify size-linked accessories of the current bike size. Position is locale-stable
    // (variant.position), so accessories match on it rather than translated titles.
    // Cache on window covers either custom element connecting first.
    emitBikeVariant() {
      var pill = this.querySelector('[data-nplus-variant-pill].is-selected');
      var variantId = pill ? pill.getAttribute('data-variant') : (this.select ? this.select.value : null);
      var position = pill ? parseInt(pill.getAttribute('data-position'), 10) : 1;
      window.nplusBikePosition = position;
      document.dispatchEvent(new CustomEvent('nplus:bike-variant-change', { detail: { variantId: variantId, position: position } }));
    }

    setBusy(b) { if (this.atc) { this.atc.disabled = b; this.atc.setAttribute('aria-busy', b ? 'true' : 'false'); } }
    showError(m) { if (this.errorEl) { this.errorEl.textContent = m; this.errorEl.hidden = false; } }
    clearError() { if (this.errorEl) { this.errorEl.textContent = ''; this.errorEl.hidden = true; } }

    onAdd() {
      this.clearError();
      var bikeVariantId = this.select ? this.select.value : null;
      if (!bikeVariantId) { this.showError(this.t('select_option', 'Please select an option.')); return; }
      if (!window.nplusCart || typeof window.nplusCart.addBundle !== 'function') { this.showError(this.t('cart_unavailable', 'Cart unavailable. Please refresh.')); return; }

      var gift = this.gift ? this.gift.getSelection() : { included: false, variantId: null };
      if (gift.included && !gift.variantId) { this.showError(this.t('choose_gift_size', 'Please choose a gift size.')); return; }

      var accessories = (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
        ? window.nplusAccessories.getSelection() : [];

      this.setBusy(true);
      var self = this;
      window.nplusCart.addBundle({ bikeVariantId: bikeVariantId, gift: gift, accessories: accessories })
        .catch(function (err) {
          var sold = /no longer available|sold out/i.test(err.message || '');
          self.showError(sold ? self.t('gift_unavailable', 'That gift is no longer available. Deselect it to add the bike on its own.') : self.t('add_failed', 'Sorry, we could not add this to your cart. Please try again.'));
        })
        .then(function () { self.setBusy(false); });
    }

    // Cash/Finance "buy now": add bike + gift + accessories, then go straight to checkout
    // (no cart flyover). Both buttons (card/Klarna) land on the same checkout; Klarna is a
    // payment method there. `method` is a label/analytics hint only.
    onBuyNow(method) {
      this.clearError();
      var bikeVariantId = this.select ? this.select.value : null;
      if (!bikeVariantId) { this.showError(this.t('select_option', 'Please select an option.')); return; }
      if (!window.nplusCart || typeof window.nplusCart.buildBundleItems !== 'function') { this.showError(this.t('cart_unavailable', 'Cart unavailable. Please refresh.')); return; }

      var gift = this.gift ? this.gift.getSelection() : { included: false, variantId: null };
      if (gift.included && !gift.variantId) { this.showError(this.t('choose_gift_size', 'Please choose a gift size.')); return; }

      var accessories = (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
        ? window.nplusAccessories.getSelection() : [];

      var items = window.nplusCart.buildBundleItems({ bikeVariantId: bikeVariantId, gift: gift, accessories: accessories });
      var self = this;
      this.setBuyNowBusy(true);

      // Cash button with the "pay on a Stripe page" theme setting on: ask the payment
      // service for a Stripe-hosted page first. It takes the SELECTION only (variant ids +
      // the visitor's country) and prices it from Shopify, so nothing shown on this page is
      // trusted for the amount. It refuses made-to-order sizes and countries it is not
      // enabled for; on any refusal or failure we carry on to Shopify checkout exactly as
      // before, so this can never be worse than the setting being off.
      if (method === 'card' && this._loadI18n().stripe_checkout === true) {
        this.requestStripePage(bikeVariantId, gift, accessories)
          .then(function (url) { window.location.assign(url); })
          .catch(function (err) {
            // A sold-out size is a real answer, not a failure: Shopify checkout would still
            // accept it (continue-selling is on) and for UK/AU offer no way to pay, so stop
            // here and say so. Anything else (network, 5xx) still falls back to checkout.
            var code = err && err.message;
            if (code === 'sold_out' || code === 'accessory_sold_out') {
              self.setBuyNowBusy(false);
              self.showError(code === 'sold_out'
                ? self.t('bike_sold_out', 'Sorry, this size has just sold out. Please choose another size.')
                : self.t('gift_unavailable', 'That gift is no longer available. Deselect it to add the bike on its own.'));
              return;
            }
            self.goToCheckout(items);
          });
        return;
      }
      this.goToCheckout(items);
    }

    checkoutSessionEndpoint() {
      var host = window.location.hostname;
      var isLocal = host === 'localhost' || host === '127.0.0.1';
      if (isLocal && window.__BACKEND_BASE) return window.__BACKEND_BASE.replace(/\/$/, '') + '/create-checkout-session';
      return '/apps/nplus-deposit/create-checkout-session';
    }

    // Resolves with the Stripe page URL, rejects on any refusal (409), error or timeout.
    requestStripePage(bikeVariantId, gift, accessories) {
      var selection = (accessories || []).map(function (a) { return { variantId: a.variantId }; });
      if (gift && gift.included && gift.variantId) selection.push({ variantId: gift.variantId });
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 12000) : null;
      return fetch(this.checkoutSessionEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        signal: ctrl ? ctrl.signal : undefined,
        body: JSON.stringify({
          variantId: bikeVariantId,
          accessories: selection,
          country: this._loadI18n().country || '',
          returnPath: window.location.pathname + window.location.search
        })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (body) {
          if (timer) clearTimeout(timer);
          if (!r.ok || !body || !body.url) throw new Error((body && body.error) || 'server_error');
          return body.url;
        });
      });
    }

    // The original Cash/Finance path: add the selection to the cart, go straight to checkout.
    goToCheckout(items) {
      var self = this;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error((e && e.description) || 'add failed'); });
        return r.json();
      }).then(function () {
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        window.location.assign(root + 'checkout');
      }).catch(function (err) {
        self.setBuyNowBusy(false);
        var sold = /no longer available|sold out/i.test(err.message || '');
        self.showError(sold ? self.t('gift_unavailable', 'That gift is no longer available. Deselect it to add the bike on its own.') : self.t('add_failed', 'Sorry, we could not add this to your cart. Please try again.'));
      });
    }

    // Stripe returns the buyer to this page with ?nplus_order=success|cancelled. Show the
    // result once, then drop the param so a reload or a shared link does not repeat it.
    showOrderNotice() {
      var el = this.querySelector('[data-nplus-order-notice]');
      if (!el) return;
      var state = null;
      try {
        var url = new URL(window.location.href);
        state = url.searchParams.get('nplus_order');
        if (state) {
          url.searchParams.delete('nplus_order');
          window.history.replaceState({}, '', url.toString());
        }
      } catch (e) { return; }
      if (state === 'success') {
        el.textContent = this.t('order_success', 'Payment received. Thank you. Our team will be in touch by email with your order and shipping details.');
        el.classList.add('nplus-bb__notice--success');
      } else if (state === 'cancelled') {
        el.textContent = this.t('order_cancelled', 'Payment cancelled. You have not been charged.');
      } else { return; }
      el.hidden = false;
      try { el.scrollIntoView({ block: 'center' }); } catch (e) { /* older browsers */ }
    }

    setBuyNowBusy(b) {
      (this.buyNow || []).forEach(function (btn) { btn.disabled = b; btn.setAttribute('aria-busy', b ? 'true' : 'false'); });
    }

    // Cash / Lease / Finance tab switcher. No-op on the reference buy-box (no tabs).
    initTabs() {
      var self = this;
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-nplus-tab]'));
      if (!this.tabs.length) return;
      this.tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { self.selectTab(tab.getAttribute('data-nplus-tab')); });
        tab.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var next = (i + (e.key === 'ArrowRight' ? 1 : self.tabs.length - 1)) % self.tabs.length;
          self.tabs[next].focus();
          self.selectTab(self.tabs[next].getAttribute('data-nplus-tab'));
        });
      });
    }

    activateHashTab() {
      var self = this;
      var selectFromHash = function () {
        var id = window.location.hash ? window.location.hash.slice(1) : '';
        if (!id) return;
        if (id === 'leasing') id = 'nplus-tab-lease'; // short form, as on VW (landing-page "leasen ab" links)
        var tab = self.querySelector('#' + id);
        if (tab && tab.hasAttribute('data-nplus-tab')) {
          var name = tab.getAttribute('data-nplus-tab');
          if (name === 'lease') self.openLease(); // tab + form reveal + scroll, see openLease()
          else self.selectTab(name);
        }
      };
      selectFromHash();
      window.addEventListener('hashchange', selectFromHash);
    }

    // Lease tab with its form revealed and scrolled into view. Unlike Finance (whose reveal
    // waits on the Creditplus form), the Lease reveal just mounts Stripe (see togglePanel), so
    // it's safe to open immediately — otherwise a deep link would still need a second click
    // to see the leasing form. Shared by the #nplus-tab-lease deep link and the lease teaser
    // links. Returns false when this buy-box has no Lease tab (non-DE market / leasing off).
    openLease() {
      var tab = this.querySelector('#nplus-tab-lease');
      if (!tab) return false;
      this.selectTab('lease');
      // Leasing pop-up mode (block setting lease_popup): open the pop-up instead of revealing
      // the inline form. <nplus-lease-lightbox> listens for clicks on [data-nplus-lease-popup].
      var popupTrigger = this.querySelector('[data-nplus-lease-popup]');
      if (popupTrigger) { popupTrigger.click(); return true; }
      var trigger = this.querySelector('[data-nplus-reveal="lease"]');
      if (trigger && trigger.getAttribute('aria-expanded') !== 'true') this.togglePanel('lease');
      this.scrollToTabs(tab.closest('[role="tablist"]') || tab);
      return true;
    }

    // Puts the Cash/Lease/Finance tab row 16px below the viewport top (the site header is
    // static and the jump-nav, below the product section, only pins once scrolled past it).
    // A deep link runs this at DOMContentLoaded, while the mobile Splide gallery above is still
    // stacked (~4,900px) before collapsing into a carousel; so until the page has loaded the
    // jump is instant and re-applied whenever the page resizes, stopping 500ms after load
    // (4s cap) or as soon as the user scrolls themselves.
    scrollToTabs(el) {
      var target = function () { return Math.max(0, Math.round(el.getBoundingClientRect().top + window.pageYOffset) - 16); };
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (document.readyState === 'complete') {
        window.scrollTo({ top: target(), behavior: reduce ? 'auto' : 'smooth' });
        return;
      }
      window.scrollTo(0, target());
      if (typeof ResizeObserver !== 'function') return;
      var ro = new ResizeObserver(function () { window.scrollTo(0, target()); });
      var userEvents = ['wheel', 'touchstart', 'keydown'];
      var stop = function () {
        ro.disconnect();
        userEvents.forEach(function (t) { window.removeEventListener(t, stop, true); });
      };
      ro.observe(document.body);
      userEvents.forEach(function (t) { window.addEventListener(t, stop, { capture: true, passive: true }); });
      window.addEventListener('load', function () { setTimeout(stop, 500); }, { once: true });
      setTimeout(stop, 4000);
    }

    // "Dienstrad-Leasing ab … pro Monat" teasers: near the title (main-product.liquid, outside
    // <nplus-buybox>) and inside the buy-box, so bound page-wide like initJobradWidget().
    // Opens the Lease form; the providers drawer (id = attribute value) is only the fallback
    // when there's no Lease tab. The JobRad logo ([data-nplus-lease-providers-open]) is separate.
    initLeaseTeasers() {
      var self = this;
      Array.prototype.forEach.call(document.querySelectorAll('[data-nplus-lease-teaser]'), function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (!self.openLease()) self.openDrawer(btn.getAttribute('data-nplus-lease-teaser'));
        });
      });
    }

    selectTab(name) {
      this.tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-nplus-tab') === name;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.classList.toggle('is-active', on);
        tab.tabIndex = on ? 0 : -1;
      });
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-tabpanel]'), function (p) {
        p.hidden = p.getAttribute('data-nplus-tabpanel') !== name;
      });
      // Stripe is mounted on reveal (togglePanel), not on tab-select — the Lease/Finance
      // forms sit behind an "Order with…" reveal, so mounting here would mount while hidden.
      this.clearError();
    }

    // Lazily parses .nplus-bb__i18n (strings + currency/locale for the subtotal formatter).
    _loadI18n() {
      if (!this._i18n) {
        try {
          var el = this.querySelector('.nplus-bb__i18n');
          this._i18n = el ? JSON.parse(el.textContent) : {};
        } catch (e) { this._i18n = {}; }
      }
      return this._i18n;
    }

    // i18n helper — reads optional cfg.i18n injected by Liquid, else English fallback.
    t(key, fallback) {
      return this._loadI18n()[key] || fallback;
    }

    // Lazily parses .nplus-bb__stock-map (variant-id → inventory_quantity, null when untracked,
    // plus the low-stock threshold). No-op container when the stock counter block is off.
    _loadStockMap() {
      if (!this._stockMap) {
        try {
          var el = this.querySelector('.nplus-bb__stock-map');
          this._stockMap = el ? JSON.parse(el.textContent) : { threshold: 0, quantities: {} };
        } catch (e) { this._stockMap = { threshold: 0, quantities: {} }; }
      }
      return this._stockMap;
    }

    // Refreshes the stock line for the given variant id on pill switch. Mirrors the Liquid
    // rules in nplus-buybox-combined.liquid: hidden when untracked or qty <= 0, low-stock
    // message (with count) at/under the threshold, in-stock message above it.
    updateStock(variantId) {
      if (!this.stockEl) return;
      var map = this._loadStockMap();
      var qty = map.quantities[variantId];
      var inStock = !(qty === null || qty === undefined || qty <= 0);
      // Pre-order bikes (exact mode): lines that only apply to a made-to-order size
      // ([data-nplus-preorder-only], e.g. "Made to order in 60 days") hide for a size in stock.
      if (map.exact) {
        Array.prototype.forEach.call(document.querySelectorAll('[data-nplus-preorder-only]'), function (el) { el.hidden = inStock; });
      }
      if (!inStock) {
        this.stockEl.hidden = true;
        return;
      }
      var i18n = this._loadI18n();
      if (this.stockTextEl) {
        var template = map.exact ? i18n.stock_count : (qty <= map.threshold ? i18n.stock_low : i18n.stock_in);
        this.stockTextEl.textContent = (template || '').replace('__COUNT__', qty);
      }
      this.stockEl.hidden = false;
    }

    // JobRad logo trigger(s) beside the lease teaser (near-title in main-product.liquid, and
    // inside this buy-box) — bound page-wide, like the old tab-snap teaser handler was, since
    // the near-title one lives outside <nplus-buybox> entirely. Opens the hidden
    // <jobrad-price-widget> element's own calculator dialog by forwarding the click to its
    // internal shadow-DOM button (the widget ships an open shadow root but no public open()
    // API), falling back to the leasing-providers drawer — whose id is the trigger's
    // [data-nplus-lease-providers-open] value — if the widget never hydrates (e.g. its
    // third-party script is slow or blocked), so the click is never a dead end.
    // Also keeps the widget's displayed price current: it's rendered once at page load for
    // the initially selected variant, but the pill picker below changes variants client-side
    // without a page reload, so data-actual-price/data-uvp-price would otherwise go stale.
    initJobradWidget() {
      var self = this;
      var triggers = Array.prototype.slice.call(document.querySelectorAll('[data-nplus-lease-providers-open]'));
      if (triggers.length) {
        triggers.forEach(function (trigger) {
          trigger.addEventListener('click', function (e) {
            e.preventDefault();
            self.openJobradWidget(trigger.getAttribute('data-nplus-lease-providers-open'), trigger);
          });
        });
      }
      // Consent given while the pre-consent overlay is open (snippets/nplus-consent-loader.liquid
      // has just injected the widget script): close it and open the JobRad calculator straight away.
      document.addEventListener('nplus:consent-loaded', function (e) {
        if (!e.detail || e.detail.mount !== 'jobrad-price-widget' || !self.jobradConsentOpen) return;
        var open = self.jobradConsentOpen;
        self.closeJobradConsent(false);
        self.openJobradWidget(open.fallbackDrawerId, open.trigger);
      });

      var priceMapEl = document.querySelector('.nplus-jobrad-widget-prices');
      if (!priceMapEl) return;
      try { this.jobradPriceMap = JSON.parse(priceMapEl.textContent); } catch (e) { return; }
      document.addEventListener('nplus:bike-variant-change', function (e) {
        var widget = document.querySelector('jobrad-price-widget');
        var entry = widget && self.jobradPriceMap[e.detail.variantId];
        if (!entry) return;
        widget.setAttribute('data-actual-price', entry.actual);
        widget.setAttribute('data-uvp-price', entry.uvp);
      });
    }

    openJobradWidget(fallbackDrawerId, trigger) {
      var self = this;
      var clickWidgetButton = function () {
        var widget = document.querySelector('jobrad-price-widget');
        var btn = widget && widget.shadowRoot && widget.shadowRoot.querySelector('button[data-variant=link]');
        if (btn) { btn.click(); return true; }
        return false;
      };
      if (clickWidgetButton()) return;

      // No widget on this template: whenDefined() would never resolve, so go straight to the drawer.
      var pendingWidget = document.querySelector('jobrad-price-widget');
      if (!pendingWidget) { self.openDrawer(fallbackDrawerId); return; }
      // Widget script not loaded yet because there's no marketing consent (nplus-consent-loader):
      // show the consent overlay under the logo instead (drawer if the overlay isn't on the page).
      if (pendingWidget.getAttribute('data-nplus-consent') === 'pending') { self.openJobradConsent(trigger, fallbackDrawerId); return; }

      // Not hydrated yet (module script still loading/rendering) — wait for it, then give up
      // and fall back to the drawer rather than leave the click doing nothing. whenDefined()
      // never settles if the script is blocked or fails, hence the separate give-up timer.
      if (typeof customElements === 'undefined' || !customElements.whenDefined) { self.openDrawer(fallbackDrawerId); return; }
      var defined = false, gaveUp = false;
      setTimeout(function () { if (!defined) { gaveUp = true; self.openDrawer(fallbackDrawerId); } }, 5000);
      customElements.whenDefined('jobrad-price-widget').then(function () {
        defined = true;
        if (gaveUp) return; // drawer already open; don't stack the calculator on top
        if (clickWidgetButton()) return;
        var widget = document.querySelector('jobrad-price-widget');
        if (!widget) { self.openDrawer(fallbackDrawerId); return; }
        var observer = new MutationObserver(function () {
          if (clickWidgetButton()) observer.disconnect();
        });
        observer.observe(widget.shadowRoot || widget, { childList: true, subtree: true });
        setTimeout(function () {
          observer.disconnect();
          if (!clickWidgetButton()) self.openDrawer(fallbackDrawerId);
        }, 2000);
      });
    }

    // Pre-consent overlay (snippets/nplus-jobrad-consent.liquid): moved next to the clicked logo
    // and shown absolutely positioned under it, so nothing in the buy box moves. Closes on Esc,
    // an outside click or its close button, returning focus to the logo.
    openJobradConsent(trigger, fallbackDrawerId) {
      var self = this;
      var box = document.querySelector('[data-nplus-jobrad-consent]');
      if (!box || !trigger) { self.openDrawer(fallbackDrawerId); return; }
      if (self.jobradConsentOpen) self.closeJobradConsent(false);
      var row = (trigger.closest && trigger.closest('.nplus-bb__lease-teaser-row')) || trigger.parentNode;
      row.classList.add('nplus-bb__lease-teaser-row--consent');
      row.appendChild(box);
      box.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-controls', box.id);
      var onKey = function (e) { if (e.key === 'Escape' || e.key === 'Esc') self.closeJobradConsent(true); };
      var onClick = function (e) {
        if (e.target.closest && e.target.closest('[data-nplus-jobrad-consent-close]')) { self.closeJobradConsent(true); return; }
        if (box.contains(e.target) || (e.target.closest && e.target.closest('[data-nplus-lease-providers-open]'))) return;
        // The cookie-settings link opens Pandectes' preferences by clicking its widget; clicks in
        // the consent manager (retargeted to <pandectes-cmp>) are not outside clicks.
        if (e.target.closest && e.target.closest('pandectes-cmp')) return;
        self.closeJobradConsent(true);
      };
      document.addEventListener('keydown', onKey);
      document.addEventListener('click', onClick);
      self.jobradConsentOpen = { box: box, row: row, trigger: trigger, fallbackDrawerId: fallbackDrawerId, onKey: onKey, onClick: onClick };
      box.focus();
    }

    closeJobradConsent(returnFocus) {
      var open = this.jobradConsentOpen;
      if (!open) return;
      this.jobradConsentOpen = null;
      open.box.hidden = true;
      open.row.classList.remove('nplus-bb__lease-teaser-row--consent');
      open.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', open.onKey);
      document.removeEventListener('click', open.onClick);
      if (returnFocus) open.trigger.focus();
    }

    openDrawer(id) {
      if (id && window.Alpine && window.Alpine.store) window.Alpine.store('drawers').open(id);
    }

    // Sets up the Intl.NumberFormat used by recalcSubtotal(), from the currency/locale that
    // Liquid injects into .nplus-bb__i18n (same pattern as nplus-leasing-calc.js).
    initMoney() {
      var cfg = this._loadI18n();
      var currency = cfg.currency || 'EUR';
      var locale = cfg.locale || undefined;
      try { this.money = new Intl.NumberFormat(locale, { style: 'currency', currency: currency }); }
      catch (e) { this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }); }
      this.decimals = this.money.resolvedOptions().maximumFractionDigits;
    }

    // Live subtotal = selected variant price + checked accessories (free items contribute 0).
    // Reads the base price from priceEl's own data-price-minor (kept current by selectPill()),
    // not from the pills, since single-variant products render no pills at all.
    recalcSubtotal() {
      if (!this.priceEl || !this.money) return;
      var baseMinor = parseInt(this.priceEl.getAttribute('data-price-minor'), 10);
      if (isNaN(baseMinor)) baseMinor = 0;
      var accMinor = 0;
      if (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function') {
        window.nplusAccessories.getSelection().forEach(function (item) {
          accMinor += item.free ? 0 : (Number(item.priceMinor) || 0);
        });
      }
      var totalMinor = baseMinor + accMinor;
      this.priceEl.textContent = this.money.format(totalMinor / Math.pow(10, this.decimals));
    }

    initReveals() {
      var self = this;
      this.reveals = Array.prototype.slice.call(this.querySelectorAll('[data-nplus-reveal]'));
      this.reveals.forEach(function (btn) {
        btn.addEventListener('click', function () { self.togglePanel(btn.getAttribute('data-nplus-reveal')); });
      });
    }

    togglePanel(name) {
      var self = this;
      var willOpen = false;
      this.reveals.forEach(function (btn) {
        var n = btn.getAttribute('data-nplus-reveal');
        var panel = self.querySelector('[data-nplus-panel="' + n + '"]');
        var open = (n === name) && !(btn.getAttribute('aria-expanded') === 'true');
        if (n === name) willOpen = open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) panel.hidden = !open;
      });
      if (willOpen) {
        var openPanel = this.querySelector('[data-nplus-panel="' + name + '"]');
        var dep = openPanel && (openPanel.querySelector('nplus-leasing-deposit') || openPanel.querySelector('nplus-card-deposit'));
        if (dep && typeof dep.ensureStripe === 'function') dep.ensureStripe();
      }
    }
  }

  if (!customElements.get('nplus-buybox')) {
    customElements.define('nplus-buybox', NplusBuybox);
  }
})();
